import { useNavigate, Link } from "react-router-dom";
import { Clock, ShieldCheck, Users, Landmark, Lock, ArrowRight, MessageCircle, FileCheck2, Briefcase, Building2, Home as HomeIcon, MapPin, Star, Laptop, UploadCloud, MessagesSquare, ClipboardCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { LOGO, HERO_BG, ADVISOR_WOMAN, ADVISOR_MAN, CONTACT_EMAIL } from "../lib/constants";

const HOME_GUIDES = [
  { to: "/declaration-impots-frontalier-suisse", title: "Déclaration impôt frontalier Suisse", desc: "Le guide complet : 2047, 2042, case 8TK, taux CHF/EUR, comptes suisses, Genève vs autres cantons." },
  { to: "/declaration-impots-frontalier-geneve", title: "Déclaration frontalier Genève", desc: "Comment déclarer un salaire genevois en France et agir sur l'impôt à la source avant le 31 mars." },
  { to: "/tou-geneve", title: "TOU Genève", desc: "Taxation ordinaire ultérieure : qui est concerné, avantages, risques, déductions et procédure." },
  { to: "/quasi-resident-geneve", title: "Quasi-résident Genève", desc: "La règle des 90 %, les revenus du foyer, les déductions accessibles et les erreurs fréquentes." },
  { to: "/rectification-impot-source-geneve", title: "Rectification impôt à la source / DRIS", desc: "Barème erroné, enfants, conjoint : quand et comment corriger votre impôt à la source genevois." },
  { to: "/formulaire-2047-suisse", title: "Formulaire 2047 Suisse", desc: "Remplir la déclaration des revenus suisses cadre par cadre, avec les bons reports." },
];

const benefits = [
  { icon: Clock, title: "Gain de temps", desc: "Nous prenons en charge l'intégralité de vos démarches fiscales." },
  { icon: ShieldCheck, title: "Sécurité fiscale", desc: "Des déclarations conformes, vérifiées et sécurisées." },
  { icon: Users, title: "Accompagnement personnalisé", desc: "Un suivi adapté à votre profil et votre situation." },
  { icon: Landmark, title: "Expertise Suisse-France", desc: "Double compétence fiscale des deux côtés de la frontière." },
  { icon: Lock, title: "Confidentialité des données", desc: "Vos informations sont traitées avec la plus stricte discrétion." },
  { icon: Laptop, title: "100 % en ligne", desc: "Toutes vos démarches à distance, sans aucun déplacement nécessaire." },
];

const audiences = [
  { icon: MapPin, title: "Frontaliers Suisse-France", desc: "Déclarations, rectifications d'impôt à la source et optimisation pour les travailleurs frontaliers.", to: "/frontaliers" },
  { icon: HomeIcon, title: "Résidents suisses", desc: "Déclaration fiscale suisse complète, déductions et vérification de votre taxation.", to: "/services" },
  { icon: FileCheck2, title: "Déclarations françaises", desc: "Déclaration de revenus française, revenus suisses, fonciers et comptes à l'étranger.", to: "/services" },
  { icon: Briefcase, title: "Indépendants en Suisse", desc: "Comptabilité de base, TVA, déclaration fiscale et accompagnement annuel.", to: "/independants-entreprises" },
  { icon: Building2, title: "Entreprises en Suisse", desc: "Fiscalité des sociétés suisses, optimisation et suivi administratif complet.", to: "/independants-entreprises" },
];

const testimonials = [
  { name: "Sophie M.", profile: "Frontalière — Genève 🇫🇷🇨🇭", rating: 5, text: "Grâce à la rectification d'impôt à la source, j'ai récupéré des déductions que j'ignorais complètement. Un accompagnement clair, rapide et très professionnel." },
  { name: "Laurent D.", profile: "Frontalier — Annemasse 🇫🇷🇨🇭", rating: 5, text: "Mes déclarations suisse et française sont enfin cohérentes. Fiscoconseils gère tout de A à Z, je gagne un temps précieux chaque année." },
  { name: "Céline R.", profile: "Résidente suisse — Vaud 🇨🇭", rating: 5, text: "Déclaration complète, conseils sur le 3e pilier et les rachats LPP : des explications simples et un vrai suivi personnalisé. Je recommande vivement." },
  { name: "Marc B.", profile: "Indépendant — Genève 🇨🇭", rating: 5, text: "En tant qu'indépendant, la TVA et la comptabilité me dépassaient. Depuis deux ans, tout est carré et mes échéances sont respectées sans stress." },
  { name: "Nathalie K.", profile: "Dirigeante de Sàrl — Lausanne 🇨🇭", rating: 4, text: "Un partenaire fiable pour notre société : déclarations dans les délais, réponses rapides et une optimisation fiscale bien expliquée." },
  { name: "Julien T.", profile: "Frontalier — Haute-Savoie 🇫🇷🇨🇭", rating: 5, text: "Simulation gratuite très détaillée, puis un devis honnête et transparent. On sent l'expérience des deux fiscalités, suisse et française." },
];

const Stars = ({ rating }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={15} className={i <= rating ? "text-[#C9A961] fill-[#C9A961]" : "text-[#D1D5DB]"} strokeWidth={1.5} />
    ))}
  </div>
);

export default function Home({ onOpenChat }) {
  const navigate = useNavigate();

  return (
    <main data-testid="home-page">
      <SEO
        title="Fiscoconseils | Expert fiscal Suisse, France et frontaliers"
        description="Déclaration fiscale frontalier suisse, impôts Suisse France, rectification impôt à la source, fiscalité indépendant et entreprise suisse. Devis personnalisé gratuit."
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-[76px]">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Vue de Genève, Suisse — fiscalité des frontaliers" fetchpriority="high" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A1128]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 w-full">
          <div className="max-w-3xl">
            <img src={LOGO} alt="Logo Fiscoconseils" className="h-24 w-24 mb-8 anim-fade-up bg-white/95 rounded-full p-1.5" />
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#C4C8CC] mb-5 anim-fade-up anim-delay-1">
              Fiscalité Suisse · France · Frontaliers
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05] anim-fade-up anim-delay-1">
              Votre expert fiscal pour <span className="text-gold">la Suisse, la France</span> et les frontaliers
            </h1>
            <p className="text-base md:text-lg text-[#D1D5DB] mt-6 leading-relaxed max-w-2xl anim-fade-up anim-delay-2">
              Déclarations fiscales, optimisation, accompagnement personnalisé et conseils fiscaux pour particuliers, frontaliers, indépendants et entreprises suisses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 anim-fade-up anim-delay-3">
              <button
                data-testid="hero-devis-btn"
                onClick={() => navigate("/devis")}
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-[#0A1128] bg-white rounded-full px-8 py-4 hover:bg-[#E5E7EB] transition-colors duration-200"
              >
                Demander un devis <ArrowRight size={16} />
              </button>
              <button
                data-testid="hero-chat-btn"
                onClick={onOpenChat}
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white border border-[#C4C8CC]/60 rounded-full px-8 py-4 hover:bg-white/10 transition-colors duration-200"
              >
                <MessageCircle size={16} strokeWidth={1.75} /> Parler à un conseiller IA
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SLOGAN */}
      <section className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <p className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#0A1128] tracking-tight max-w-4xl italic">
            « L'expertise fiscale au service des frontaliers, particuliers et entreprises suisses. »
          </p>
          <div data-testid="home-pricing-strip" className="flex flex-wrap gap-x-10 gap-y-4 mt-10">
            {[
              ["Simulation", "Gratuite"],
              ["Déclaration suisse", "dès CHF 100.-"],
              ["Déclaration française", "dès 100 €"],
              ["Conseils fiscaux", "dès CHF 200.-"],
            ].map(([label, price]) => (
              <div key={label}>
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#6B7280]">{label}</p>
                <p className="font-heading text-xl font-semibold text-[#0A1128] mt-1">{price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS / PROMO */}
      <section data-testid="stats-section" className="bg-[#0A1128] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white max-w-3xl leading-tight">
            Votre patrimoine, <span className="text-gold">deux pays,</span> une expertise.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
            <div data-testid="stat-experience">
              <p className="font-heading text-4xl sm:text-5xl text-gold">15+</p>
              <p className="text-sm font-semibold text-white mt-2">ans d'expérience</p>
              <p className="text-xs text-[#9CA3AF] mt-1">en fiscalité Suisse-France</p>
            </div>
            <div data-testid="stat-clients">
              <p className="font-heading text-4xl sm:text-5xl text-gold">1'200+</p>
              <p className="text-sm font-semibold text-white mt-2">clients accompagnés</p>
              <p className="text-xs text-[#9CA3AF] mt-1">particuliers, frontaliers et entreprises</p>
            </div>
            <div data-testid="stat-fidelisation">
              <p className="font-heading text-4xl sm:text-5xl text-gold">98%</p>
              <p className="text-sm font-semibold text-white mt-2">taux de fidélisation</p>
              <p className="text-xs text-[#9CA3AF] mt-1">nos clients nous font confiance chaque année</p>
            </div>
            <div data-testid="stat-bilateral">
              <p className="font-heading text-4xl sm:text-5xl">🇨🇭 🇫🇷</p>
              <p className="text-sm font-semibold text-white mt-2">double expertise bilatérale</p>
              <p className="text-xs text-[#9CA3AF] mt-1">maîtrise des fiscalités suisse et française</p>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-4">Pourquoi Fiscoconseils</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128] max-w-2xl">
          Une fiscalité maîtrisée, <span className="text-gold">un esprit serein</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {benefits.map((b, i) => (
            <div
              key={b.title}
              data-testid={`benefit-card-${i}`}
              className="bg-white border border-[#C4C8CC]/50 rounded-2xl p-7 shadow-[0_8px_32px_rgba(10,17,40,0.06)] hover:-translate-y-1 transition-transform duration-300"
            >
              <b.icon size={28} strokeWidth={1.5} className="text-[#0A1128]" />
              <h3 className="text-base font-bold text-[#0A1128] mt-5">{b.title}</h3>
              <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* POUR QUI */}
      <section className="bg-[#0A1128] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-4">Pour qui ?</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">
            Un accompagnement dédié à chaque profil fiscal
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {audiences.map((a, i) => (
              <Link
                key={a.title}
                to={a.to}
                data-testid={`audience-card-${i}`}
                className={`group bg-white/[0.04] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.08] transition-colors duration-300 ${i === 0 ? "lg:col-span-2" : ""}`}
              >
                <a.icon size={30} strokeWidth={1.5} className="text-[#C4C8CC]" />
                <h3 className="text-lg font-bold text-white mt-6">{a.title}</h3>
                <p className="text-sm text-[#9CA3AF] mt-3 leading-relaxed">{a.desc}</p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#C4C8CC] mt-6 group-hover:gap-3 transition-[gap] duration-200">
                  Découvrir <ArrowRight size={15} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="grid grid-cols-2 gap-5">
          <img src={ADVISOR_WOMAN} alt="Conseillère fiscale Fiscoconseils" loading="lazy" width="640" height="340" className="rounded-2xl object-cover w-full h-[340px] shadow-[0_8px_32px_rgba(10,17,40,0.12)]" />
          <img src={ADVISOR_MAN} alt="Conseiller fiscal Fiscoconseils" className="rounded-2xl object-cover w-full h-[340px] mt-10 shadow-[0_8px_32px_rgba(10,17,40,0.12)]" />
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-4">Notre engagement</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128]">
            Un cabinet dédié à votre tranquillité fiscale
          </h2>
          <p className="text-base text-[#4B5563] mt-6 leading-relaxed">
            Fiscoconseils accompagne les travailleurs frontaliers, résidents suisses, indépendants et entreprises en Suisse dans leurs déclarations fiscales, rectifications, optimisations et démarches administratives.
          </p>
          <p className="text-base text-[#4B5563] mt-4 leading-relaxed">
            Notre objectif : vous faire gagner du temps, sécuriser votre situation fiscale et vous offrir un accompagnement clair, fiable et personnalisé.
          </p>
          <ul className="mt-8 space-y-3">
            {["Analyse personnalisée de votre situation", "Déclarations fiscales suisses et françaises", "Spécialiste des frontaliers Suisse-France", "Conseils fiscaux clairs et professionnels", "Devis adapté à votre profil"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm font-medium text-[#0A1128]">
                <FileCheck2 size={16} strokeWidth={1.75} className="text-[#6B7280] shrink-0" /> {t}
              </li>
            ))}
          </ul>
          <button
            data-testid="about-devis-btn"
            onClick={() => navigate("/devis")}
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0A1128] rounded-full px-8 py-4 hover:bg-[#1A2342] transition-colors duration-200"
          >
            Obtenir mon devis personnalisé <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* 100% EN LIGNE */}
      <section data-testid="online-section" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="bg-[#0A1128] rounded-3xl p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-4">Zéro déplacement</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">
              Toutes vos démarches, <span className="text-gold">100 % en ligne</span>
            </h2>
            <p className="text-base text-[#D1D5DB] mt-6 leading-relaxed">
              Grâce à votre espace client sécurisé, tout se fait à distance : transmettez vos documents fiscaux, suivez l'avancement de votre dossier et échangez directement avec votre conseiller. Pas besoin de vous déplacer.
            </p>
            <p className="text-base text-[#D1D5DB] mt-4 leading-relaxed">
              Un avantage précieux si vous manquez de temps, vivez loin de nos bureaux ou ne pouvez pas vous déplacer.
            </p>
            <Link
              data-testid="online-client-btn"
              to="/espace-client"
              className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] bg-white rounded-full px-8 py-4 hover:bg-[#E5E7EB] transition-colors duration-200"
            >
              Accéder à mon espace client <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: UploadCloud, title: "Déposez vos documents", desc: "Envoi sécurisé de vos pièces fiscales depuis chez vous." },
              { icon: ClipboardCheck, title: "Suivez votre dossier", desc: "Statut en temps réel, de la demande au devis signé." },
              { icon: MessagesSquare, title: "Échangez avec votre conseiller", desc: "Messagerie directe et confidentielle, sans rendez-vous." },
            ].map((s) => (
              <div key={s.title} className="bg-white/[0.05] border border-white/10 rounded-2xl p-6">
                <s.icon size={26} strokeWidth={1.5} className="text-[#C4C8CC]" />
                <h3 className="text-sm font-bold text-white mt-4">{s.title}</h3>
                <p className="text-xs text-[#9CA3AF] mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section data-testid="testimonials-section" className="bg-[#F8F9FA] border-t border-[#E5E7EB] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-4">Témoignages</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0A1128] max-w-2xl">
                Plus de 1'200 clients <span className="text-gold">nous font confiance</span>
              </h2>
            </div>
            <div data-testid="testimonials-rating" className="flex items-center gap-4 shrink-0">
              <p className="font-heading text-5xl text-[#0A1128]">4.9<span className="text-2xl text-[#6B7280]">/5</span></p>
              <div>
                <Stars rating={5} />
                <p className="text-xs text-[#6B7280] mt-1.5">Note moyenne de satisfaction<br />98% de taux de fidélisation</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                data-testid={`testimonial-card-${i}`}
                className="bg-white border border-[#C4C8CC]/50 rounded-2xl p-8 shadow-[0_8px_32px_rgba(10,17,40,0.06)] hover:-translate-y-1 transition-transform duration-300 flex flex-col"
              >
                <Stars rating={t.rating} />
                <p className="text-sm text-[#374151] leading-relaxed mt-5 flex-1">« {t.text} »</p>
                <div className="mt-6 pt-5 border-t border-[#E5E7EB]">
                  <p className="text-sm font-bold text-[#0A1128]">{t.name}</p>
                  <p className="text-xs text-[#6B7280] mt-0.5">{t.profile}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDES SEO */}
      <section data-testid="home-guides-section" className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-4">Guides fiscaux frontaliers</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128] max-w-2xl">
              Déclaration impôt frontalier Suisse : comprendre avant de déclarer
            </h2>
          </div>
          <Link to="/guides-fiscaux-frontaliers" data-testid="home-guides-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] border border-[#C4C8CC] rounded-full px-6 py-3 hover:bg-[#F8F9FA] transition-colors duration-200 shrink-0">Tous les guides <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {HOME_GUIDES.map((g) => (
            <Link key={g.to} to={g.to} data-testid={`home-guide-${g.to.slice(1)}`} className="group bg-white border border-[#C4C8CC]/50 rounded-2xl p-7 hover:border-[#0A1128]/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
              <h3 className="text-base md:text-lg font-bold text-[#0A1128] group-hover:text-[#1A2342]">{g.title}</h3>
              <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{g.desc}</p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] mt-4">Lire <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" /></span>
            </Link>
          ))}
        </div>
        <p className="text-sm text-[#6B7280] mt-10 max-w-3xl leading-relaxed">
          Fiscaliste frontalier à Genève, Annemasse, Saint-Julien-en-Genevois et dans le Pays de Gex : Fiscoconseils accompagne les frontaliers de Haute-Savoie et de l'Ain pour leur déclaration française, la rectification de l'impôt à la source (DRIS), la TOU et le statut de quasi-résident. Essayez aussi nos <Link to="/outils-frontaliers" className="text-[#0A1128] underline underline-offset-4">outils gratuits</Link>.
        </p>
      </section>

      {/* CTA */}
      <section className="bg-[#F8F9FA] border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128]">
              Prêt à sécuriser votre situation fiscale ?
            </h2>
            <p className="text-base text-[#6B7280] mt-3">Réponse rapide, analyse confidentielle et devis sans engagement. Contact : {CONTACT_EMAIL}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <button data-testid="cta-devis-btn" onClick={() => navigate("/devis")} className="text-sm font-semibold text-white bg-[#0A1128] rounded-full px-8 py-4 hover:bg-[#1A2342] transition-colors duration-200">
              Demander un devis
            </button>
            <button data-testid="cta-chat-btn" onClick={onOpenChat} className="text-sm font-semibold text-[#0A1128] border border-[#C4C8CC] rounded-full px-8 py-4 hover:bg-white transition-colors duration-200">
              Parler au conseiller IA
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
